import { wrapSuccess, wrapError, ResponseOrError } from '../../types/responses';
import { NOTICE_ELEMENT_PREFIX } from '../../pagination/constants';
import { mapAsync } from '../../helpers/array/async';
import { UserNoticeModel } from '../../model/objects/userNoticeModel';
import { getModelFromSnapshot } from '../../model';
import { IndesignServerClient } from '../../indesign/request';
import {
  EFirebaseContext,
  ENotice,
  ESnapshotExists,
  ETemplate
} from '../../types';
import { AutoLayoutRequestElement } from '../../types/api';
import { DisplayParams } from '../../types/notice';
import { PublisherPaginationSettings } from '../../types/publisherPaginationSettings';

const MAX_VARIANT_COLUMNS = 6;
const MIN_OPTIONAL_VARIANT_HEIGHT = 100;

export const findValidNoticeVariants = (
  variants: DisplayParams[],
  template: ESnapshotExists<ETemplate>
) => {
  const validVariants = variants
    .filter((variant, idx) => {
      if (variant.columns > MAX_VARIANT_COLUMNS) {
        return false;
      }

      // Always allow the first variant since it is the default
      if (idx === 0) {
        return true;
      }

      const variantHeightInPixels =
        (variant.height ?? 0) * (template.data().styles?.pointsPerInch ?? 0);

      // Don't allow variants that are too short
      if (variantHeightInPixels < MIN_OPTIONAL_VARIANT_HEIGHT) {
        return false;
      }

      const templateHeightInPixels = template.data().styles?.pageHeight ?? 0;
      if (variantHeightInPixels > templateHeightInPixels) {
        return false;
      }

      return true;
    })
    .sort((a, b) => (a.columns < b.columns ? -1 : 1));

  return validVariants;
};

export const getVariantsForNotices = async (
  firebaseContext: EFirebaseContext,
  publisherPaginationSettings: ESnapshotExists<PublisherPaginationSettings>,
  notices: ESnapshotExists<ENotice>[],
  autoSolverTemplate: ESnapshotExists<ETemplate>,
  indesignServerClient: IndesignServerClient,
  domParser: typeof DOMParser
): Promise<ResponseOrError<AutoLayoutRequestElement[]>> => {
  const [variantsError, noticeBlocks] = await mapAsync(
    notices,
    async notice => {
      const noticeModel = getModelFromSnapshot(
        UserNoticeModel,
        firebaseContext,
        notice
      );
      const [variantsError, variants] =
        await noticeModel.getDisplayParamVariants(
          indesignServerClient,
          domParser,
          {
            disableVariants:
              publisherPaginationSettings.data().variants?.disabled
          }
        );
      if (variantsError) {
        return wrapError(variantsError);
      }

      const validVariants = findValidNoticeVariants(
        variants,
        autoSolverTemplate
      );

      if (validVariants.length === 0) {
        return wrapError(
          new Error(`No valid variants found for notice ${notice.id}`)
        );
      }

      const noticeBlock = {
        id: `${NOTICE_ELEMENT_PREFIX}-${notice.id}`,
        variants: validVariants.map(variant => ({
          width: variant.columns,
          height: Math.round(
            (variant.height ?? 0) *
              (autoSolverTemplate.data().styles?.pointsPerInch ?? 0)
          ),
          columns: variant.columns
        })),
        order_type: 'notice'
      };
      return wrapSuccess(noticeBlock);
    }
  );
  if (variantsError) {
    return wrapError(variantsError);
  }

  return wrapSuccess(noticeBlocks);
};
